import SwitchCampaignState from "../../../ui/SwitchCampaignState"
import { ICampaignDataColumns } from "../../sponsoredProducts/types/table"
import { campaignStates, matchTypesFromAmazon, stateByCampaignId } from "../../../types/customers"
import useSearchColumn from "../../../hooks/useSearchColumn"
import { getMatchTypesFromAmazonOptions } from "../../../services/campaign.service"
import { matchByMatchTypeFromAmazon } from "../../../helper/filter/filters.helpers"
import { COLUMNS_SIZE_MAP } from "../constants/table.constants"
import CampaignTags from "../components/CampaignTag"

import { useActions } from "../../../hooks/useActions"
import AsinCvr from "./useCampaignColumnsComponents.ts/AsinCvr"
import { compareNullableNumbers } from "./helpers/sort.helper"

interface IUseCampaignColumnsProps {
  minLimitedCPCByCampaignIdMap: Record<string, number>
}

const useCampaignColumns = ({ minLimitedCPCByCampaignIdMap }: IUseCampaignColumnsProps) => {
  const { fetchChangeCampaignsState } = useActions()

  const searchColumnCampaignName = useSearchColumn<ICampaignDataColumns>({
    dataIndex: "campaignName",
  })

  const changeCampaignState = (value: boolean, campaignId: number) => {
    const state = value ? campaignStates.ENABLED : campaignStates.PAUSED
    const stateByCampaignId: stateByCampaignId[] = [{ campaignId: campaignId, state }]
    fetchChangeCampaignsState(stateByCampaignId)
  }

  return [
    {
      title: "Active",
      render: (row: ICampaignDataColumns) => (
        <SwitchCampaignState
          onChange={(value) => changeCampaignState(value, row.campaignId)}
          campaignState={row.campaignStatus}
        />
      ),
      filters: [
        { text: "Active", value: campaignStates.ENABLED },
        { text: "Paused", value: campaignStates.PAUSED },
      ],
      onFilter: (value, record) => record.campaignStatus === value,
      width: COLUMNS_SIZE_MAP["1"],
    },
    {
      title: "Target Acos",
      render: () => "-",
      width: COLUMNS_SIZE_MAP["2"],
    },
    {
      title: "Campaigns",
      dataIndex: "campaignName",
      key: "campaignName",
      ...searchColumnCampaignName,
      width: COLUMNS_SIZE_MAP["3"],
    },
    {
      title: "Tags",
      dataIndex: "tags",
      width: COLUMNS_SIZE_MAP["4"],
      render: (tags) => <CampaignTags campaignTags={tags} />,
    },
    {
      title: "Match Type",
      dataIndex: "matchType",
      filters: getMatchTypesFromAmazonOptions(),
      onFilter: (value, record) => matchByMatchTypeFromAmazon(value, record?.matchType),
      render: (text, record) => record?.matchType || "—",
      width: COLUMNS_SIZE_MAP["5"],
    },
    {
      title: "Bid Adjustment",
      dataIndex: "keywordBid",
      sorter: (a, b) => a.keywordBid - b.keywordBid,
      key: "keywordBid",
      render: (text) => `${text ? text : "0"}$`,
      width: COLUMNS_SIZE_MAP["6"],
    },
    {
      title: "Target CPC In Amazon",
      dataIndex: "targetCPCAmazon",
      key: "targetCPCAmazon",
      render: (text) => (text ? text : "-"),
      width: COLUMNS_SIZE_MAP["7"],
    },
    {
      title: "Target CPC",
      dataIndex: "targetCPC",
      key: "targetCPC",
      render: (text) => (text !== undefined ? text.toString() : "-"),
      width: COLUMNS_SIZE_MAP["8"],
    },
    {
      title: "Best CPC",
      dataIndex: "bestCPC",
      key: "bestCPC",
      render: (text) => (text !== undefined ? text.toString() : "-"),
      width: COLUMNS_SIZE_MAP["9"],
    },
    {
      title: "Limited CPC",
      dataIndex: "limitedCPC",
      key: "limitedCPC",
      render: (text) => (text !== undefined ? text.toString() : "-"),
      width: COLUMNS_SIZE_MAP["10"],
    },
    {
      title: "New Bid Adjustment",
      render: (campaign: ICampaignDataColumns) => {
        const newBid = minLimitedCPCByCampaignIdMap[String(campaign.campaignId)]
        return newBid || newBid === 0 ? `$${newBid.toFixed(2)}` : "-"
      },
      width: COLUMNS_SIZE_MAP["11"],
    },
    {
      title: "Impressions",
      dataIndex: "totalImpressions",
      key: "totalImpressions",
      sorter: (a, b) => a.totalImpressions - b.totalImpressions,
      width: COLUMNS_SIZE_MAP["12"],
    },
    {
      title: "Clicks",
      dataIndex: "totalClicks",
      key: "totalClicks",
      sorter: (a, b) => a.totalClicks - b.totalClicks,
      width: COLUMNS_SIZE_MAP["13"],
    },
    {
      title: "CTR%",
      dataIndex: "totalCtr",
      key: "totalCtr",
      render: (text) => `${text || 0}%`,
      sorter: (a, b) => a.totalCtr - b.totalCtr,
      width: COLUMNS_SIZE_MAP["14"],
    },
    {
      title: "Spend",
      dataIndex: "totalSpend",
      key: "totalSpend",
      render: (text) => `${text}$`,
      sorter: (a, b) => a.totalSpend - b.totalSpend,
      width: COLUMNS_SIZE_MAP["15"],
    },
    {
      title: "Sales",
      dataIndex: "totalSales",
      key: "totalSales",
      render: (text) => `${text}$`,
      sorter: (a, b) => a.totalSales - b.totalSales,
      width: COLUMNS_SIZE_MAP["16"],
    },
    {
      title: "Orders",
      dataIndex: "totalOrders",
      key: "totalOrders",
      sorter: (a, b) => a.totalOrders - b.totalOrders,
      width: COLUMNS_SIZE_MAP["17"],
    },
    {
      title: "Units",
      dataIndex: "totalUnits",
      key: "totalUnits",
      sorter: (a, b) => a.totalUnits - b.totalUnits,
      width: COLUMNS_SIZE_MAP["18"],
    },
    {
      title: "CVR%",
      dataIndex: "totalCvr",
      key: "totalCvr",
      render: (text) => `${text}%`,
      sorter: (a, b) => a.totalCvr - b.totalCvr,
      width: COLUMNS_SIZE_MAP["19"],
    },
    {
      title: "Project SQP CVR%",
      render: (campaign) => (
        <AsinCvr
          asinCvr={campaign.asinCvr}
          isExact={campaign.matchType === matchTypesFromAmazon.EXACT}
        />
      ),
      sorter: (a, b) => compareNullableNumbers(a.asinCvr, b.asinCvr),
      width: COLUMNS_SIZE_MAP["20"],
    },
    {
      title: "Market CVR%",
      render: (campaign) => (
        <AsinCvr
          asinCvr={campaign.marketCvr}
          isExact={campaign.matchType === matchTypesFromAmazon.EXACT}
        />
      ),
      sorter: (a, b) => compareNullableNumbers(a.marketCvr, b.marketCvr),
      width: COLUMNS_SIZE_MAP["21"],
    },
    {
      title: "ACOS%",
      dataIndex: "totalAcos",
      key: "totalAcos",
      render: (text) => `${text}%`,
      sorter: (a, b) => a.totalAcos - b.totalAcos,
      width: COLUMNS_SIZE_MAP["22"],
    },
    {
      title: "CPC",
      dataIndex: "totalCpc",
      key: "totalCpc",
      render: (text) => `${text}$`,
      sorter: (a, b) => a.totalCpc - b.totalCpc,
      width: COLUMNS_SIZE_MAP["23"],
    },
    {
      title: "ROAS",
      dataIndex: "totalRoas",
      key: "totalRoas",
      render: (text) => `${text}`,
      sorter: (a, b) => a.totalRoas - b.totalRoas,
      width: COLUMNS_SIZE_MAP["24"],
    },
  ]
}

export default useCampaignColumns
